import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import type { WalletBalance, WalletTransaction } from '../../services/api/types';
import { walletService } from '../../services/api';
import { r } from '../../theme/riderTheme';
import {
  PageCard,
  PrimaryButton,
  ButtonChevron,
} from '../../theme/surfacePrimitives';

interface WalletCardProps {
  balance: WalletBalance | null;
  loading: boolean;
  onLoadWallet: () => void;
  onViewTransactions: () => void;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ringSpin = keyframes`
  to { transform: rotate(360deg); }
`;

const WalletShell = styled(PageCard)`
  padding: 18px 18px 16px;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-bottom: 14px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) =>
      `linear-gradient(90deg, ${r(theme).surface.edge}, transparent)`};
  }
`;

const EyebrowMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1px solid ${({ theme }) => r(theme).accent.limeDeep};
  color: ${({ theme }) => r(theme).accent.ok};
  background: ${({ theme }) => r(theme).surface.raisedSelected};
  border-radius: 50%;
  /* Devanagari rupee mark (रु) — fall back to a Devanagari-capable system
     font so the glyph renders cleanly even on machines without Geist's
     Devanagari coverage. Match the Khalti mark's intent (currency-glyph-as-eyebrow)
     while staying in the rider lime/ok palette. */
  font-family: 'Geist', 'Noto Sans Devanagari', system-ui, sans-serif;
  font-weight: 600;
  font-size: 11px;
  line-height: 1;
`;

const BalanceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
`;

const BalanceCurrency = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 500;
  color: ${({ theme }) => r(theme).text.faint};
`;

const BalanceAmount = styled.span`
  font-family: 'Geist', sans-serif;
  font-size: 32px;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1;
  color: ${({ theme }) => r(theme).text.primary};
`;

const LastUpdated = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ theme }) => r(theme).accent.ok};
    box-shadow: 0 0 6px ${({ theme }) => r(theme).accent.ok};
  }
`;

const ActivitySection = styled.section`
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => r(theme).surface.edge};
  animation: ${fadeIn} 380ms cubic-bezier(0.2, 0.8, 0.2, 1) 80ms backwards;
`;

const ActivityHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

const ActivityLabel = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const ViewAllLink = styled.button.attrs({ type: 'button' as const })`
  background: transparent;
  border: 0;
  padding: 4px 0;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.primary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 160ms ease;

  &:hover {
    color: ${({ theme }) => r(theme).accent.ok};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
    border-radius: 2px;
  }

  &::after {
    content: '→';
    font-family: 'Geist', sans-serif;
    font-size: 11px;
    letter-spacing: 0;
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActivityRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  gap: 12px;

  & + & {
    border-top: 1px solid ${({ theme }) => r(theme).surface.edge};
  }
`;

const ActivityInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const ActivityDescription = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => r(theme).text.primary};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActivityScooter = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => r(theme).text.muted};
  line-height: 1.3;
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActivityDate = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-top: 3px;
`;

type AmountTone = 'credit' | 'debit' | 'neutral';

const amountToneOf = (txType: string): AmountTone => {
  if (txType === 'topup' || txType === 'refund') return 'credit';
  if (txType === 'pack_purchase') return 'debit';
  return 'neutral';
};

const ActivityAmount = styled.div<{ $tone: AmountTone }>`
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;

  ${({ $tone, theme }) =>
    $tone === 'credit' &&
    css`
      color: ${r(theme).accent.ok};
    `}

  ${({ $tone, theme }) =>
    $tone === 'debit' &&
    css`
      color: ${r(theme).text.primary};
    `}

  ${({ $tone, theme }) =>
    $tone === 'neutral' &&
    css`
      color: ${r(theme).text.muted};
    `}
`;

const EmptyActivity = styled.div`
  text-align: center;
  padding: 16px;
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => r(theme).text.muted};
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px dashed ${({ theme }) => r(theme).surface.edge};
  border-radius: 10px;
`;

const LoadingShell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 28px 12px;
`;

const LoadingSpinner = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => r(theme).surface.edge};
  border-top-color: ${({ theme }) => r(theme).accent.lime};
  animation: ${ringSpin} 0.9s linear infinite;
`;

const LoadingLabel = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.muted};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
`;

const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-NP', { month: 'short', day: 'numeric' });
};

const formatActivityAmount = (transaction: WalletTransaction): string => {
  const tone = amountToneOf(transaction.type);
  const prefix = tone === 'debit' ? '−' : tone === 'credit' ? '+' : '';
  return `${prefix}${walletService.formatCurrency(transaction.amount_npr || 0)}`;
};

const WalletCard: React.FC<WalletCardProps> = ({
  balance,
  loading,
  onLoadWallet,
  onViewTransactions,
}) => {
  if (loading && !balance) {
    return (
      <WalletShell>
        <Eyebrow>
          <EyebrowMark>रु</EyebrowMark>
          Wallet · Balance
        </Eyebrow>
        <LoadingShell>
          <LoadingSpinner />
          <LoadingLabel>Loading wallet</LoadingLabel>
        </LoadingShell>
      </WalletShell>
    );
  }

  const hasTxns =
    balance?.recent_transactions && balance.recent_transactions.length > 0;

  return (
    <WalletShell>
      <Eyebrow>
        <EyebrowMark>रु</EyebrowMark>
        Wallet · Balance
      </Eyebrow>

      <BalanceRow>
        <BalanceCurrency>NPR</BalanceCurrency>
        <BalanceAmount>
          {balance?.current_balance !== undefined
            ? (balance.current_balance ?? 0).toLocaleString('en-IN')
            : '0'}
        </BalanceAmount>
      </BalanceRow>

      {balance?.last_updated && (
        <LastUpdated>Updated {formatRelativeDate(balance.last_updated)}</LastUpdated>
      )}

      <ButtonRow>
        <PrimaryButton onClick={onLoadWallet} disabled={loading}>
          <span>Load Wallet</span>
          <ButtonChevron>+</ButtonChevron>
        </PrimaryButton>
      </ButtonRow>

      {balance && (
        <ActivitySection>
          <ActivityHeader>
            <ActivityLabel>Recent Activity</ActivityLabel>
            {hasTxns && (
              <ViewAllLink onClick={onViewTransactions}>View All</ViewAllLink>
            )}
          </ActivityHeader>

          {hasTxns ? (
            <ActivityList>
              {balance.recent_transactions!.slice(0, 3).map(transaction => {
                const tone = amountToneOf(transaction.type);
                const scooterLabel = transaction.scooter_label || transaction.scooter_vin;
                return (
                  <ActivityRow key={transaction.transaction_id}>
                    <ActivityInfo>
                      <ActivityDescription>{transaction.notes}</ActivityDescription>
                      {transaction.type === 'pack_purchase' && scooterLabel && (
                        <ActivityScooter>Scooter · {scooterLabel}</ActivityScooter>
                      )}
                      <ActivityDate>
                        {formatRelativeDate(transaction.timestamp)}
                      </ActivityDate>
                    </ActivityInfo>
                    <ActivityAmount $tone={tone}>
                      {formatActivityAmount(transaction)}
                    </ActivityAmount>
                  </ActivityRow>
                );
              })}
            </ActivityList>
          ) : (
            <EmptyActivity>No recent transactions</EmptyActivity>
          )}
        </ActivitySection>
      )}
    </WalletShell>
  );
};

export default WalletCard;
